import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    MapPin,
    Building2,
    FileText,
    Users as UsersIcon,
    CreditCard,
    Clock
} from 'lucide-react';

export default function BranchHeader({
    branch,
    organization,
    users,
    subscribedModuleCodes = [],
    activeTab,
    setActiveTab,
}) {
    const navigate = useNavigate();
    const isActive = branch?.active;

    const tabs = [
        { id: 'details', label: 'Branch Details', icon: FileText },
        { id: 'users', label: 'Users', icon: UsersIcon },
        { id: 'subscription', label: 'Subscription Modules', icon: CreditCard }
    ];

    return (
        <div className="relative bg-gradient-to-br from-[#232F3E] via-indigo-700 to-indigo-800 text-white">
            
            {/* Soft Background Glow */}
            <div className="absolute inset-0 opacity-20 pointer-events-none">
                <div className="absolute -top-20 left-1/3 w-96 h-96 bg-[#f08804] rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#131921] rounded-full blur-3xl" />
            </div>

            <div className="relative px-8 py-6">
                
                {/* Top Bar */}
                <div className="flex items-center justify-between mb-6">
                    
                    {/* Back Button */}
                    <button
                        onClick={() => navigate('/platform/organizations')}
                        className="flex items-center gap-2 px-4 py-2 bg-white text-[#C45500] hover:bg-slate-100 rounded-lg transition-all shadow-md text-xs font-semibold"
                    >
                        <ArrowLeft size={16} />
                        Back to Organizations
                    </button>

                    {/* Branch Status */}
                    <span
                        className={`px-3 py-1 text-xs font-bold rounded-md shadow-sm ${
                            isActive
                                ? 'bg-emerald-500 text-white'
                                : 'bg-rose-500 text-white'
                        }`}
                    >
                        {isActive ? 'Active Branch' : 'Inactive Branch'}
                    </span>
                </div>

                {/* Branch Info */}
                <div className="flex items-center justify-between flex-wrap gap-6">
                    
                    {/* Left Info */}
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center backdrop-blur-sm border border-white/20 shadow-lg">
                            <MapPin size={28} strokeWidth={2.5} />
                        </div>

                        <div>
                            <h1 className="text-3xl font-extrabold mb-1 tracking-tight">
                                {branch?.name}
                            </h1>

                            <div className="flex flex-wrap items-center gap-5 text-[#FFD89E] text-xs">
                                
                                <div className="flex items-center gap-1.5">
                                    <Building2 size={13} />
                                    <span className="font-medium">
                                        {organization?.name || 'Unknown Organization'}
                                    </span>
                                </div>

                                {branch?.addresses?.[0] && (
                                    <div className="flex items-center gap-1.5">
                                        <MapPin size={13} />
                                        <span className="font-medium">
                                            {branch.addresses[0].city}, {branch.addresses[0].state}
                                        </span>
                                    </div>
                                )}

                                <div className="flex items-center gap-1.5">
                                    <Clock size={13} />
                                    <span className="font-medium">
                                        ID: {branch?.id?.slice(0, 8)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="flex gap-4 flex-wrap">
                        
                        <div className="bg-white text-[#C45500] rounded-lg px-5 py-3 shadow-lg min-w-[100px]">
                            <div className="text-2xl font-extrabold">
                                {users.length}
                            </div>
                            <div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wide">
                                Users
                            </div>
                        </div>

                        <div className="bg-white text-[#C45500] rounded-lg px-5 py-3 shadow-lg min-w-[100px]">
                            <div className="text-2xl font-extrabold">
                                {subscribedModuleCodes.length}
                            </div>
                            <div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wide">
                                Modules
                            </div>
                        </div>

                        <div className="bg-white text-[#C45500] rounded-lg px-5 py-3 shadow-lg min-w-[100px]">
                            <div className="text-2xl font-extrabold">
                                {branch?.dbName ? '✓' : '✗'}
                            </div>
                            <div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wide">
                                Database
                            </div>
                        </div>

                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-3 mt-8 flex-wrap">
                    {tabs.map(tab => {
                        const Icon = tab.icon;
                        const isActiveTab = activeTab === tab.id;

                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-xs transition-all duration-200 shadow-sm ${
                                    isActiveTab
                                        ? 'bg-white text-[#C45500] shadow-md'
                                        : 'bg-[#FFF4E6]0/40 text-white hover:bg-[#FFF4E6]0/60'
                                }`}
                            >
                                <Icon size={15} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

            </div>
        </div>
    );
}